/**
 * BYK-4: PBR Materials Service
 *
 * Provides physically-based rendering materials for the 3D scene.
 * Features:
 * - Style palette presets (Warm 2025, Nordic, Mediterranean, etc.)
 * - Material caching for performance
 * - Surface type mapping (countertop, cabinet-door, flooring, etc.)
 * - PBRMaterial creation with metallic/roughness workflow
 */

import * as BABYLON from "@babylonjs/core";
import type { MaterialConfig, SurfaceType, StylePalette } from "@byk/shared";
import { STYLE_PALETTE_PRESETS } from "@byk/shared";

// =============================================================================
// Constants
// =============================================================================

/** Default style palette */
const DEFAULT_PALETTE_NAME = "warm-2025";

// =============================================================================
// Types
// =============================================================================

interface CachedMaterial {
  material: BABYLON.PBRMaterial;
  createdAt: number;
}

// =============================================================================
// Cache (Module-level)
// =============================================================================

/** Cache of PBR materials keyed by palette+surface combination */
const materialCache = new Map<string, CachedMaterial>();

/** Current active palette name */
let currentPaletteName = DEFAULT_PALETTE_NAME;

// =============================================================================
// Public API
// =============================================================================

/**
 * Get the current active style palette
 */
export function getCurrentPalette(): StylePalette {
  const preset = STYLE_PALETTE_PRESETS[currentPaletteName as keyof typeof STYLE_PALETTE_PRESETS];
  return {
    id: currentPaletteName,
    ...preset,
  };
}

/**
 * Set the active style palette
 */
export function setCurrentPalette(paletteName: string): void {
  if (paletteName in STYLE_PALETTE_PRESETS) {
    currentPaletteName = paletteName;
    // Clear material cache when palette changes
    clearMaterialCache();
  } else {
    console.warn(`[PBR Materials] Unknown palette: ${paletteName}`);
  }
}

/**
 * Get a PBR material for a specific surface type
 * Uses the current active palette
 */
export function getPBRMaterial(
  surfaceType: SurfaceType,
  scene: BABYLON.Scene,
  suffix?: string
): BABYLON.PBRMaterial {
  const cacheKey = `${currentPaletteName}-${surfaceType}${suffix ? `-${suffix}` : ""}`;

  // Return cached material if available
  if (materialCache.has(cacheKey)) {
    return materialCache.get(cacheKey)!.material;
  }

  // Get palette config
  const palette = getCurrentPalette();
  const config = palette.materials[surfaceType];

  // Create PBR material
  const material = createPBRMaterial(cacheKey, config, scene);

  // Cache the material
  materialCache.set(cacheKey, {
    material,
    createdAt: Date.now(),
  });

  return material;
}

/**
 * Create a PBR material with custom configuration
 * (for cases where palette isn't used)
 */
export function createPBRMaterial(
  name: string,
  config: MaterialConfig,
  scene: BABYLON.Scene
): BABYLON.PBRMaterial {
  const material = new BABYLON.PBRMaterial(name, scene);

  // Base color
  material.albedoColor = BABYLON.Color3.FromHexString(config.baseColor);

  // Metallic-roughness workflow
  material.metallic = config.metallic;
  material.roughness = config.roughness;

  // Emissive (for accents/lighting)
  if (config.emissiveColor) {
    material.emissiveColor = BABYLON.Color3.FromHexString(config.emissiveColor);
    material.emissiveIntensity = 0.3;
  }

  // Ambient occlusion intensity
  if (config.ambientOcclusionIntensity !== undefined) {
    material.ambientTextureStrength = config.ambientOcclusionIntensity;
  }

  // Enable for best quality
  material.useRadianceOverAlpha = true;
  material.useSpecularOverAlpha = true;
  material.environmentIntensity = 0.7;

  return material;
}

/**
 * Get PBR material for room floor
 */
export function getFloorMaterial(scene: BABYLON.Scene): BABYLON.PBRMaterial {
  return getPBRMaterial("flooring", scene, "floor");
}

/**
 * Get PBR material for room walls
 */
export function getWallMaterial(scene: BABYLON.Scene): BABYLON.PBRMaterial {
  return getPBRMaterial("wall", scene, "room-wall");
}

/**
 * Get PBR material for room ceiling
 */
export function getCeilingMaterial(scene: BABYLON.Scene): BABYLON.PBRMaterial {
  // Use wall material with slight adjustments for ceiling
  const material = getPBRMaterial("wall", scene, "ceiling");
  return material;
}

/**
 * Get PBR material for cabinet body
 */
export function getCabinetBodyMaterial(scene: BABYLON.Scene): BABYLON.PBRMaterial {
  return getPBRMaterial("cabinet-body", scene);
}

/**
 * Get PBR material for cabinet doors
 */
export function getCabinetDoorMaterial(scene: BABYLON.Scene): BABYLON.PBRMaterial {
  return getPBRMaterial("cabinet-door", scene);
}

/**
 * Get PBR material for countertops
 */
export function getCountertopMaterial(scene: BABYLON.Scene): BABYLON.PBRMaterial {
  return getPBRMaterial("countertop", scene);
}

/**
 * Get PBR material for backsplash
 */
export function getBacksplashMaterial(scene: BABYLON.Scene): BABYLON.PBRMaterial {
  return getPBRMaterial("backsplash", scene);
}

/**
 * Get PBR material for appliances
 */
export function getApplianceMaterial(scene: BABYLON.Scene): BABYLON.PBRMaterial {
  return getPBRMaterial("appliance", scene);
}

/**
 * Get PBR material for hardware (handles, knobs)
 */
export function getHardwareMaterial(scene: BABYLON.Scene): BABYLON.PBRMaterial {
  return getPBRMaterial("hardware", scene);
}

/**
 * Get PBR material for a volume based on product category
 */
export function getVolumeMaterial(
  categorySlug: string | undefined,
  scene: BABYLON.Scene,
  volumeId: string
): BABYLON.PBRMaterial {
  // Map category to surface type
  let surfaceType: SurfaceType = "cabinet-door"; // Default

  if (categorySlug) {
    const lowerCategory = categorySlug.toLowerCase();
    if (lowerCategory.includes("sink") || lowerCategory.includes("tap")) {
      surfaceType = "hardware"; // Metallic
    } else if (lowerCategory.includes("oven") || lowerCategory.includes("cooktop") || lowerCategory.includes("fridge")) {
      surfaceType = "appliance";
    } else if (lowerCategory.includes("surface") || lowerCategory.includes("counter")) {
      surfaceType = "countertop";
    } else if (lowerCategory.includes("cabinet")) {
      surfaceType = "cabinet-door";
    }
  }

  return getPBRMaterial(surfaceType, scene, volumeId);
}

/**
 * Create a selected state overlay for a material
 * Returns a new material with selection highlight
 */
export function createSelectedMaterial(
  baseMaterial: BABYLON.PBRMaterial,
  _scene: BABYLON.Scene
): BABYLON.PBRMaterial {
  // Clone the base material
  const selectedMaterial = baseMaterial.clone(`${baseMaterial.name}-selected`) as BABYLON.PBRMaterial;

  // Add blue tint for selection
  selectedMaterial.albedoColor = BABYLON.Color3.Lerp(
    baseMaterial.albedoColor,
    new BABYLON.Color3(0.2, 0.4, 0.9), // Selection blue
    0.3 // 30% blend
  );

  // Slight emissive glow
  selectedMaterial.emissiveColor = new BABYLON.Color3(0.1, 0.2, 0.4);
  selectedMaterial.emissiveIntensity = 0.5;

  return selectedMaterial;
}

/**
 * Clear the material cache
 */
export function clearMaterialCache(): void {
  for (const [, cached] of materialCache) {
    cached.material.dispose();
  }
  materialCache.clear();
}

/**
 * Get list of available palette names
 */
export function getAvailablePalettes(): string[] {
  return Object.keys(STYLE_PALETTE_PRESETS);
}
